import type { Tenant } from '@vehix/portal/services/api/tenants';

/**
 * Paying-customer detection — single source of truth, used by Tenant Setup
 * (row highlight + KPI) and User Setup (Avg Users / Tenant denominator).
 *
 * A tenant is a paying customer when ALL of:
 *   1. status === 'active'
 *   2. tenant_type === 'dealer' (internal/test/partner are excluded)
 *   3. tenant_name does NOT contain 'demo' (case-insensitive)
 *   4. tenant_name is NOT in NON_PAYING_TENANT_NAMES (curated deny-list
 *      for prospects / legacy demo accounts whose names don't say "demo")
 *
 * This is a HEURISTIC. The deny-list is a stopgap until a dedicated
 * billing/customer flag lands in the tenants schema — once that exists,
 * collapse this whole predicate to `t.is_paying_customer === true`.
 */

/**
 * Tenant names (case-insensitive, trimmed) that LOOK like active dealer
 * accounts but are actually prospects or legacy demo setups. Add to this
 * list as new prospect tenants are discovered.
 */
const NON_PAYING_TENANT_NAMES = new Set([
  'orange buick gmc',
  'wade ford'
]);

/**
 * Returns true when the given tenant qualifies as a paying customer.
 */
export function isPayingCustomer(tenant: Pick<Tenant, 'status' | 'tenant_type' | 'tenant_name'>): boolean {
  if (tenant.status !== 'active') return false;
  if (tenant.tenant_type !== 'dealer') return false;
  const nameLower = tenant.tenant_name?.toLowerCase().trim() ?? '';
  if (nameLower.includes('demo')) return false;
  if (NON_PAYING_TENANT_NAMES.has(nameLower)) return false;
  return true;
}

/**
 * Returns true for tenants Vehix uses itself — `tenant_type === 'internal'`, or
 * any tenant whose name contains "demo" (case-insensitive). Their zones are
 * proposal scaffolding, not sold territory, so the Zone Map still shows those
 * zones as available.
 */
export function isInternalUseTenant(tenant: Pick<Tenant, 'tenant_type' | 'tenant_name'>): boolean {
  if (tenant.tenant_type === 'internal') return true;
  return tenant.tenant_name?.toLowerCase().includes('demo') ?? false;
}

/**
 * Returns the set of tenant IDs that qualify as paying customers from a
 * given list. Use this from React `useMemo` so the result is stable
 * across renders.
 */
export function getPayingCustomerIds(
  tenants: ReadonlyArray<Pick<Tenant, 'id' | 'status' | 'tenant_type' | 'tenant_name'>> | undefined | null
): Set<string> {
  const ids = new Set<string>();
  (tenants ?? []).forEach(t => {
    if (isPayingCustomer(t)) ids.add(t.id);
  });
  return ids;
}
